import { Outlet, NavLink } from 'react-router-dom';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, FileText, ClipboardList, Inbox, Receipt } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { cn } from '@/lib/utils';

// -----------------------------------------------------------------------------
// Branding context — propagates the agency white-label manifest to child pages
// so they can re-use the logo, agency name, support email, etc. without
// refetching. The primary color is additionally applied as a CSS custom
// property on the layout root so `bg-brand-500` / `text-brand-600` utility
// classes inherit the agency brand instead of the default AIAds blue.
// -----------------------------------------------------------------------------

export interface ClientBranding {
  agency_name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string | null;
  support_email: string | null;
  support_phone: string | null;
  locale: string;
  footer_text: string | null;
}

const DEFAULT_BRANDING: ClientBranding = {
  agency_name: 'Panel klienta',
  logo_url: null,
  primary_color: '#2E5BFF',
  secondary_color: null,
  support_email: null,
  support_phone: null,
  locale: 'pl',
  footer_text: null,
};

const ClientBrandingContext = createContext<ClientBranding>(DEFAULT_BRANDING);

export function useClientBranding(): ClientBranding {
  return useContext(ClientBrandingContext);
}

/**
 * Shift a hex color toward a given lightness step. Used to derive the
 * -600/-700 variants from the single primary color the agency configures.
 * If parsing fails we just return the original hex — better than crashing.
 */
function shadeHex(hex: string, percent: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const num = parseInt(clean, 16);
  if (Number.isNaN(num)) return hex;

  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;

  r = Math.round((t - r) * p + r);
  g = Math.round((t - g) * p + g);
  b = Math.round((t - b) * p + b);

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function ClientLayout() {
  const { user, logout } = useAuth();

  const brandingQuery = useQuery<ClientBranding>({
    queryKey: ['client', 'branding'],
    queryFn: async () => {
      const res = await api.get('/client/branding');
      return res.data.data as ClientBranding;
    },
    staleTime: 10 * 60_000,
  });

  const branding: ClientBranding = useMemo(
    () => brandingQuery.data ?? DEFAULT_BRANDING,
    [brandingQuery.data],
  );

  // Apply the agency's primary color to the brand-* CSS custom properties
  // so Tailwind utilities like bg-brand-500 / text-brand-700 pick it up
  // inside this layout subtree only (we scope via inline style on the root).
  const brandStyle = useMemo(() => {
    const base = branding.primary_color || '#2E5BFF';
    return {
      // tailwind.config.js defines these as CSS vars — see brand palette.
      ['--brand-50' as any]: shadeHex(base, 95),
      ['--brand-100' as any]: shadeHex(base, 85),
      ['--brand-400' as any]: shadeHex(base, 15),
      ['--brand-500' as any]: base,
      ['--brand-600' as any]: shadeHex(base, -15),
      ['--brand-700' as any]: shadeHex(base, -30),
    };
  }, [branding.primary_color]);

  // Sync the document title with the agency name so browser tabs are branded.
  useEffect(() => {
    if (branding.agency_name) {
      document.title = `${branding.agency_name} — Panel klienta`;
    }
  }, [branding.agency_name]);

  const tabs = [
    { to: '/client', label: 'Przegląd', icon: LayoutDashboard, end: true },
    { to: '/client/reports', label: 'Raporty', icon: FileText },
    { to: '/client/proposals', label: 'Propozycje', icon: ClipboardList },
    { to: '/client/inbox', label: 'Inbox', icon: Inbox },
    { to: '/client/invoices', label: 'Faktury', icon: Receipt },
  ];

  return (
    <ClientBrandingContext.Provider value={branding}>
      <div className="min-h-screen bg-slate-50" style={brandStyle}>
        <header className="bg-white border-b border-slate-200">
          <div className="container flex h-16 items-center justify-between gap-6">
            <div className="flex items-center gap-3 min-w-0">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={branding.agency_name}
                  className="h-9 w-auto max-w-[180px] object-contain"
                />
              ) : (
                <div
                  className="text-lg font-bold truncate"
                  style={{ color: branding.primary_color }}
                >
                  {branding.agency_name}
                </div>
              )}
            </div>

            <nav className="flex gap-1 flex-1 justify-center">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <NavLink
                    key={tab.to}
                    to={tab.to}
                    end={tab.end}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition',
                        isActive
                          ? 'bg-brand-50 text-brand-700'
                          : 'text-slate-600 hover:bg-slate-100',
                      )
                    }
                  >
                    <Icon size={16} />
                    <span>{tab.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            <div className="flex items-center gap-3">
              <div className="text-right hidden md:block">
                <div className="text-sm font-medium text-slate-700 leading-tight">
                  {user?.name ?? 'Klient'}
                </div>
                {user?.email && (
                  <div className="text-xs text-slate-400 leading-tight">{user.email}</div>
                )}
              </div>
              <button
                onClick={() => logout()}
                className="text-sm text-slate-500 hover:text-slate-700 transition"
              >
                Wyloguj
              </button>
            </div>
          </div>
        </header>

        <main className="container py-8">
          <Outlet />
        </main>

        {branding.footer_text && (
          <footer className="container py-6 text-xs text-slate-400 text-center">
            {branding.footer_text}
          </footer>
        )}
      </div>
    </ClientBrandingContext.Provider>
  );
}
